import { useQuery } from '@tanstack/react-query';
import { useAnalyticsCore } from './useAnalyticsCore';
import { AuthFilter } from '@/components/analytics/AuthFilterButtons';

/**
 * React Query wrapper for analytics data with automatic caching
 * Caches data for 5 minutes and automatically refetches on stale data
 */
export const useAnalyticsQuery = (
  selectedRange: number,
  userId: string | undefined,
  authFilter: AuthFilter = 'all'
) => {
  const queryKey = ['analytics', selectedRange, userId, authFilter];
  
  const {
    pageStats,
    actionStats,
    dailyStats,
    platformStats,
    browserStats,
    osStats,
    timeStats,
    languageStats,
    referrerStats,
    sessionMetrics,
    exitPages,
    utmStats,
    totalEvents,
    loading,
    loadingPageStats,
    loadingActionStats,
    loadingDailyStats,
    loadingPlatformStats,
    loadingBrowserStats,
    loadingOSStats,
    loadingTimeStats,
    loadingLanguageStats,
    loadingTotalStats,
    loadingReferrerStats,
    loadingSessionMetrics,
    loadingExitPages,
    loadingUTMStats,
    hasErrors,
    errors,
    cleanupOldData,
    retryAllData,
    loadData,
  } = useAnalyticsCore(selectedRange, userId, authFilter);

  // Use React Query for caching with the data from useAnalyticsCore
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      // Return current data immediately, loadData is called by useAnalyticsCore
      return {
        pageStats,
        actionStats,
        dailyStats,
        platformStats,
        browserStats,
        osStats,
        timeStats,
        languageStats,
        referrerStats,
        sessionMetrics,
        exitPages,
        utmStats,
        totalEvents,
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    refetchOnWindowFocus: false, // Don't refetch on window focus for analytics
    enabled: true,
  });

  return {
    // Data
    pageStats,
    actionStats,
    dailyStats,
    platformStats,
    browserStats,
    osStats,
    timeStats,
    languageStats,
    referrerStats,
    sessionMetrics,
    exitPages,
    utmStats,
    totalEvents,
    
    // Loading states
    loading,
    loadingPageStats,
    loadingActionStats,
    loadingDailyStats,
    loadingPlatformStats,
    loadingBrowserStats,
    loadingOSStats,
    loadingTimeStats,
    loadingLanguageStats,
    loadingTotalStats,
    loadingReferrerStats,
    loadingSessionMetrics,
    loadingExitPages,
    loadingUTMStats,
    
    // Errors
    hasErrors,
    errors,
    
    // Functions
    cleanupOldData,
    retryAllData,
    loadData,
    
    // React Query status
    isCached: query.isStale === false,
    isRefetching: query.isRefetching,
  };
};
